"use client"

import type { MaskitoOptions } from "@maskito/core"
import type { MaskitoDateMode } from "@maskito/kit"
import { maskitoDateOptionsGenerator, maskitoWithPlaceholder } from "@maskito/kit"
import { useMaskito } from "@maskito/react"
import { format, parse, parseISO } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { useTranslations } from "next-intl"
import * as React from "react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { createDateFromString, extractUTCDateAsLocal } from "@/utils/date-utils"

type DayPickerProps = {
  className?: string
  date: Date
  onDateChange: (date: Date) => void
  min?: Date | string
  max?: Date | string
  disableAllDatesWithoutRange?: boolean
}

const PLACEHOLDER: MaskitoDateMode = "dd/mm/yyyy"

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false
  }
  return !Number.isNaN(date.getTime())
}

export function DayPicker({ className, date, onDateChange, min, max, disableAllDatesWithoutRange = false }: DayPickerProps) {
  // Verificar si se deben deshabilitar todas las fechas cuando no hay rango definido
  const shouldDisableAllDates = disableAllDatesWithoutRange && !min && !max
  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState(() => date ? format(date, "dd/MM/yyyy") : "")
  const [month, setMonth] = React.useState<Date | undefined>(date)
  const t = useTranslations("auto_form.date")

  // Sincronizar el valor del input cuando la prop date cambia
  const updateValueFromDate = React.useCallback((newDate: Date | undefined) => {
    if (newDate && isValidDate(newDate)) {
      const formattedDate = format(newDate, "dd/MM/yyyy")
      setValue(formattedDate)
      setMonth(newDate)
    }
    else {
      setValue("")
    }
  }, [])

  React.useEffect(() => {
    updateValueFromDate(date)
  }, [date, updateValueFromDate])

  // Convertir fechas de string a Date sin conversión de zona horaria para las opciones de máscara
  const minDateForMask = min ? (typeof min === "string" ? (min.includes("T") ? extractUTCDateAsLocal(min) : createDateFromString(min)) : min) : undefined
  const maxDateForMask = max ? (typeof max === "string" ? (max.includes("T") ? extractUTCDateAsLocal(max) : createDateFromString(max)) : max) : undefined

  const dateOptions = React.useMemo(() => maskitoDateOptionsGenerator({
    mode: PLACEHOLDER,
    separator: "/",
    min: minDateForMask ? parseISO(String(minDateForMask)) : undefined,
    max: maxDateForMask ? parseISO(String(maxDateForMask)) : undefined,
  }), [minDateForMask, maxDateForMask])

  const {
    plugins, // plugins keeps caret inside actual value and remove placeholder on blur
    ...placeholderOptions
    // pass 'true' as second argument to add plugin to hide placeholder when input is not focused
  } = maskitoWithPlaceholder(PLACEHOLDER, true)

  const mask = React.useMemo(() => {
    return {
      ...dateOptions,
      plugins: plugins.concat(dateOptions.plugins),
      preprocessors: [
        // Always put it BEFORE all other preprocessors
        ...placeholderOptions.preprocessors,
        ...dateOptions.preprocessors,
      ],
      postprocessors: [
        ...dateOptions.postprocessors,
        // Always put it AFTER all other postprocessors
        ...placeholderOptions.postprocessors,
      ],
    } satisfies Required<MaskitoOptions>
  }, [dateOptions, placeholderOptions.postprocessors, placeholderOptions.preprocessors, plugins])

  const maskedRefInput = useMaskito({ options: mask })

  const defaultMonth = React.useMemo(() => {
    if (date !== undefined)
      return date
    if (min)
      return typeof min === "string" ? (min.includes("T") ? extractUTCDateAsLocal(min) : createDateFromString(min)) || new Date() : min
    return new Date()
  }, [date, min])

  function handleDateChange(date: Date | undefined) {
    if (date) {
      onDateChange(date)
      setMonth(date)
      setValue(format(date, "dd/MM/yyyy"))
    }
  }

  return (
    <div className="relative flex gap-2">
      <Input
        ref={maskedRefInput}
        className="bg-background pr-10"
        id="date"
        onChange={(e) => {
          const inputValue = e.target.value
          setValue(inputValue)

          // Only parse and update if we have a complete date (dd/MM/yyyy format)
          if (inputValue.length === 10 && inputValue.includes("/")) {
            const parsedDate = parse(inputValue, "dd/MM/yyyy", new Date())
            if (isValidDate(parsedDate)) {
              onDateChange(parsedDate)
              setMonth(parsedDate)
            }
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault()
            setOpen(true)
          }
        }}
        placeholder={value}
        value={value}
      />
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              "absolute top-1/2 right-2 size-6 -translate-y-1/2",
              className,
            )}
            disabled={shouldDisableAllDates}
            variant="ghost"
          >
            <CalendarIcon className="size-4" />
            <span className="sr-only">{t("select_date")}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          alignOffset={-8}
          className="h-[350px] w-auto overflow-hidden p-0 " // Dav0: Dejar el h-350px para que no de brincos al ir cambiando de meses
          sideOffset={10}
        >
          <Calendar
            autoFocus
            defaultMonth={defaultMonth}
            maxDate={typeof max === "string" ? (max.includes("T") ? extractUTCDateAsLocal(max) : createDateFromString(max)) || undefined : max}
            minDate={typeof min === "string" ? (min.includes("T") ? extractUTCDateAsLocal(min) : createDateFromString(min)) || undefined : min}
            mode="single"
            month={month}
            onMonthChange={setMonth}
            onSelect={handleDateChange}
            required
            selected={date}
            weekStartsOn={1} // Monday as the first day of the week
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
